import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { Button } from "@/components/ui/button";
import { Plus, Search } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";

import NotesList from "../components/clinical-notes/NotesList";
import NoteForm from "../components/clinical-notes/NoteForm";
import NoteDetail from "../components/clinical-notes/NoteDetail";
import PatientSelector from "../components/clinical-notes/PatientSelector";

function useURLQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function ClinicalNotes() {
  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);

  const [selectedPatient, setSelectedPatient] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const query = useURLQuery();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const noteId = query.get("id");


  // Fetch clinical notes
  const { data: notes = [], isLoading } = useQuery({
    queryKey: ["clinical-notes"],
    queryFn: () => apiClient.get("/api/clinical-notes"),
  });

  // Fetch patients for the selector
  const { data: patients = [] } = useQuery({
    queryKey: ["patients"],
    queryFn: () => apiClient.get("/api/patients"),
  });

  useEffect(() => {
    if (noteId && notes.length > 0) {
      // Case-insensitive matching since URL params get lowercased
      const noteToShow = notes.find((n) => n.id.toLowerCase() === noteId.toLowerCase());
      setSelectedNote(noteToShow || null);
    } else if (!noteId) {
      setSelectedNote(null);
    }
  }, [noteId, notes, isLoading]);

  // Create mutation for creating/updating notes
  const saveNoteMutation = useMutation({
    mutationFn: (data) => {
      if (editingNote) {
        return apiClient.put(`/api/clinical-notes/${editingNote.id}`, data);
      } else {
        return apiClient.post("/api/clinical-notes", data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["clinical-notes"]);
      handleCloseForm();
    },
    onError: (error) => {
      console.error("Error saving note:", error);
    },
  });

  // Delete mutation
  const deleteNoteMutation = useMutation({
    mutationFn: (id) => apiClient.delete(`/api/clinical-notes/${id}`),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries(["clinical-notes"]);
      if (selectedNote?.id === deletedId) {
        navigate(createPageUrl("ClinicalNotes"));
      }
    },
    onError: (error) => {
      console.error("Error deleting note:", error);
    },
  });

  const handleOpenForm = (note = null) => {
    setEditingNote(note);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setEditingNote(null);
    setShowForm(false);
  };

  const handleSaveNote = async (data) => {
    saveNoteMutation.mutate(data);
  };

  const handleDeleteNote = async (id) => {
    if (window.confirm("Are you sure you want to delete this clinical note?")) {
      deleteNoteMutation.mutate(id);
    }
  };

  const handleSelectNote = (note) => {
    const url = createPageUrl(`ClinicalNotes?id=${note.id}`);
    navigate(url);
  };

  const filteredNotes = notes.filter((note) => {
    const patientMatch =
      selectedPatient === "all" || note.patientId === selectedPatient;
    const searchMatch =
      searchTerm === "" ||
      note.consultation_notes
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      note.diagnosis?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.treatment_plan?.toLowerCase().includes(searchTerm.toLowerCase());
    return patientMatch && searchMatch;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Clinical Notes
          </h1>
          <p className="text-slate-600">
            Document and manage patient encounters
          </p>
        </div>
        <Button
          onClick={() => handleOpenForm()}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Note
        </Button>
      </div>

      {/* Detail View or List View */}
      {selectedNote ? (
        <NoteDetail
          note={selectedNote}
          onClose={() => navigate(createPageUrl("ClinicalNotes"))}
          onEdit={handleOpenForm}
          onDelete={handleDeleteNote}
        />
      ) : noteId && isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-slate-600">Loading note...</div>
        </div>
      ) : noteId && !isLoading && !selectedNote ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-slate-600">Note not found</div>
          <Button 
            onClick={() => navigate(createPageUrl("ClinicalNotes"))}
            className="ml-4"
          >
            Back to Notes
          </Button>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-slate-200 rounded-md bg-white/60 backdrop-blur-sm"
              />
            </div>
            <PatientSelector
              patients={patients}
              selectedPatient={selectedPatient}
              onPatientChange={setSelectedPatient}
              label=""
              placeholder="Filter by patient..."
            />
          </div>

          <NotesList
            notes={filteredNotes}
            isLoading={isLoading}
            onSelectNote={handleSelectNote}
            onEditNote={handleOpenForm}
            onDeleteNote={handleDeleteNote}
          />
        </>
      )}

      {/* Form Modal */}
      {showForm && (
        <NoteForm
          note={editingNote}
          onSave={handleSaveNote}
          onClose={handleCloseForm}
          patients={patients}
          isLoading={saveNoteMutation.isLoading}
        />
      )}
    </div>
  );
}
